import { Component, Input, OnInit, input, viewChild } from '@angular/core';
import { JkBaseComponent } from 'jk-service';

/**
 * @class Candlestick
 * @description 代表單一 K 線資料的資料模型。
 */
export class Candlestick {
  /** 開盤價 */
  open: number;
  /** 收盤價 */
  close: number;
  /** 最高價 */
  high: number;
  /** 最低價 */
  low: number;

  /**
   * Candlestick 的建構函式。
   * @param {number} open - 開盤價。
   * @param {number} close - 收盤價。
   * @param {number} high - 最高價。
   * @param {number} low - 最低價。
   */
  constructor(open: number, close: number, high: number, low: number) {
    this.open = open;
    this.close = close;
    this.high = high;
    this.low = low;
  }
}

/**
 * @class CandlestickChart
 * @description 負責在 Canvas 上繪製 K 線圖的核心類別。
 */
export class CandlestickChart {
  /** HTML Canvas 元素 */
  canvas: HTMLCanvasElement;
  /** Canvas 2D 繪圖上下文 */
  ctx!: CanvasRenderingContext2D;
  /** 圖表 Y 軸最大值 */
  maxValue: number = 100; // 假設最大值
  /** 圖表 Y 軸最小值 */
  minValue: number = 0; // 假設最小值
  /** 是否只顯示單一根 K 線 */
  onlyOne: boolean = true;
  /** K 線寬度 */
  width: number = 12;
  /** K 線高度 */
  height: number = 30;
  /** 縮放比例 */
  zoom: number = 1;

  /**
   * CandlestickChart 的建構函式。
   * @param {string} canvasId - Canvas 元素的 ID。
   */
  constructor(canvasId: string) {
    this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d')!;
    }
  }
  /**
   * 檢查並設定 K 線資料的數值範圍。
   * @param {Candlestick[]} list - K 線資料陣列。
   */
  checkRange(list:Candlestick[]){
    if (this.onlyOne) {
      this.maxValue = 30;
      this.minValue = 0;
    }
    let max = 0;
    let min = 999999;
    for(let i=0;i<list.length;i++){
      let item = list[i];
      max = Math.max(max, item.high);
      min = Math.min(min, item.low);
    };

  }

  /**
   * 繪製單一根 K 線。
   * @param {Candlestick} candlestick - 要繪製的 K 線資料。
   * @param {number} x - K 線在 Canvas 上的 X 軸起點。
   * @param {number} width - K 線的寬度。
   */
  drawCandlestick(candlestick: Candlestick, x: number, width: number) {
    const { open, close, high, low } = candlestick;
    const color = close >= open ? 'red' : 'green';

    // Draw high-low line
    this.ctx.beginPath();
    this.ctx.moveTo(x + width / 2, this.getY(high));
    this.ctx.lineTo(x + width / 2, this.getY(low));
    this.ctx.strokeStyle = color;
    this.ctx.stroke();

    // Draw open-close rectangle
    this.ctx.fillStyle = color;
    this.ctx.fillRect(
      x,
      this.getY(Math.max(open, close)),
      width,
      Math.abs(this.getY(open) - this.getY(close))
    );
  }

  /**
   * 將價格數值轉換為 Canvas 上的 Y 座標。
   * @param {number} value - 價格數值。
   * @returns {number} - 對應的 Y 座標。
   */
  getY(value: number): number {
    const canvasHeight = this.canvas.height;// TODO: minValue maxValue 要再調整
    let y = canvasHeight - ((value - this.minValue) / (this.maxValue - this.minValue)) * canvasHeight;
    return y;
  }

  /**
   * 繪製完整的 K 線圖。
   * @param {Candlestick[]} candlesticks - 要繪製的 K 線資料陣列。
   */
  draw(candlesticks: Candlestick[]) {
    const width = this.canvas.width / candlesticks.length;
    candlesticks.forEach((candlestick, index) => {
      this.drawCandlestick(candlestick, index * width, width * 0.8);
    });
  }
}

@Component({
  selector: 'app-candlestick',
  standalone: true,
  imports: [],
  templateUrl: './candlestick.component.html',
  styleUrl: './candlestick.component.css',
})
/**
 * @class CandlestickComponent
 * @extends JkBaseComponent
 * @implements OnInit
 * @description 用於顯示股票 K 線圖（蠟燭圖）的 Angular 元件。
 */
export class CandlestickComponent extends JkBaseComponent implements OnInit {
  /** Canvas 元素的 ViewChild 參考 */
  readonly candlestickCanvas = viewChild.required<HTMLCanvasElement>('candlestickCanvas');

  /** 內部使用的 onlyOne 旗標 */
  _onlyOne: boolean = true;
  /**
   * @property {boolean} onlyOne
   * @description 是否只顯示單一根 K 線。
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() get onlyOne(): boolean {
    return this._onlyOne;
  }
  set onlyOne(value: boolean) {
    this._onlyOne = value;
    this.chart.onlyOne = value;
  }

  /** 內部使用的 K 線資料列表 */
  _itemList:Candlestick[] = [];
  /**
   * @property {Candlestick[]} itemList
   * @description K 線資料陣列。當資料更新時，會自動觸發圖表重繪。
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() get itemList() {
    return this._itemList;
  }
  set itemList(value:Candlestick[]) {
    this._itemList = value;
    if(this.chart){
      this.chart.checkRange(this._itemList);
      if (this.chart) this.chart.draw(this._itemList);
    }
  }

  // width default for candlestick (was @Input() override width: number = 12; — now uses JkBaseComponent's model signal, set in constructor)

  /**
   * @property {number} height
   * @description 元件高度。
   */
  readonly height = input<number>(30);

  /** CandlestickChart 的實例，用於繪圖。 */
  chart!: CandlestickChart;

  /**
   * CandlestickComponent 的建構函式。
   */
  constructor() {
    super();
    this.width.set(12);
  }

  /**
   * @method ngAfterViewInit
   * @description Angular 生命週期鉤子，在 View 初始化後執行。
   *              用於初始化 Canvas 圖表並繪製初始資料。
   */
  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.chart = new CandlestickChart('candlestickCanvas');
    this.chart.draw(this.itemList);
  }
}
